import { Controller } from "@hotwired/stimulus";

// Applies active-state to the redesigned admin nav on page load.
//
// The cached nav (_sections_redesigned_cached) renders generic, URL-agnostic HTML
// because it's fragment-cached, so the active item / open section / active tab must
// be derived in the browser from the current path + pattern config (passed as
// values). The non-cached nav (_sections_redesigned) already has active-state
// server-rendered; it only needs to honor a one-shot tab override stored in
// sessionStorage (set when a favorite is clicked, so the user stays on the Home tab
// after navigating).
//
// Mode is inferred from whether tabPatterns were provided: cached nav passes them,
// the non-cached nav does not.
export default class extends Controller {
  static values = {
    path: String,
    sectionPatterns: { type: Object, default: {} },
    tabPatterns: { type: Object, default: {} },
  };

  connect() {
    if (Object.keys(this.tabPatternsValue).length > 0) {
      this.#applyCachedActiveState();
    } else {
      this.#applyTabOverride();
    }
  }

  // Non-cached nav: active-state is already server-rendered, so just honor a pending
  // tab override (if any).
  #applyTabOverride() {
    const override = sessionStorage.getItem("admin-nav-tab-override");
    if (!override) return;
    sessionStorage.removeItem("admin-nav-tab-override");
    this.#showTab(override);
  }

  // Cached nav: derive active item, open section, and active tab from the path.
  #applyCachedActiveState() {
    this.element.querySelectorAll("[data-nav-item-path]").forEach((el) => {
      if (this.#itemMatches(el.dataset.navItemKey, el.dataset.navItemPath)) {
        const cls = el.dataset.navActiveClass;
        if (cls) el.classList.add(cls);
        el.setAttribute("data-nav-active", "");
      }
    });

    this.element.querySelectorAll("details[data-nav-section-label]").forEach((details) => {
      const label = details.dataset.navSectionLabel;
      const hasActiveDescendant = details.querySelector("[data-nav-active]") !== null;
      const patterns = this.sectionPatternsValue[label] || [];
      const patternMatches = patterns.some((p) => this.pathValue.indexOf(p) === 0);

      if (hasActiveDescendant || patternMatches) {
        const cls = details.dataset.navActiveClass;
        if (cls) details.classList.add(cls);
        details.setAttribute("open", "");
      }
    });

    this.#showTab(this.#activeTab());
  }

  // Reveal the given tab's panel (and hide the rest) and toggle the rail icons'
  // active classes. The rail icons live outside this controller's element, so they
  // are queried from the document.
  #showTab(current) {
    this.element.querySelectorAll("[data-nav-tab-panel]").forEach((panel) => {
      if (panel.dataset.navTabPanel === current) panel.removeAttribute("hidden");
      else panel.setAttribute("hidden", "");
    });
    document.querySelectorAll("[data-nav-tab]").forEach((el) => {
      const cls = el.dataset.navActiveClass;
      if (!cls) return;
      const active = el.dataset.navTab === current;
      cls.split(" ").forEach((c) => {
        if (active) el.classList.add(c);
        else el.classList.remove(c);
      });
    });
  }

  #activeTab() {
    const override = sessionStorage.getItem("admin-nav-tab-override");
    if (override) {
      sessionStorage.removeItem("admin-nav-tab-override");
      return override;
    }
    const match = Object.keys(this.tabPatternsValue).find((key) => this.#tabMatches(key));
    return match || "crm";
  }

  // Does some OTHER nav item point at exactly the current path?
  #hasExactMatchElsewhere(itemPath) {
    const path = this.pathValue;
    return [...this.element.querySelectorAll("[data-nav-item-path]")].some((el) => {
      const other = el.dataset.navItemPath;
      if (!other || other === itemPath) return false;
      return path === other || path === other.replace(/\/$/, "");
    });
  }

  #tabMatches(tabKey) {
    const rules = this.tabPatternsValue[tabKey];
    if (!rules) return false;
    const included = rules.include.some((p) => this.pathValue.indexOf(p) === 0);
    const excluded = (rules.exclude || []).some((p) => this.pathValue.indexOf(p) === 0);
    return included && !excluded;
  }

  #itemMatches(key, itemPath) {
    if (!itemPath) return false;
    const path = this.pathValue;
    const stripped = itemPath.replace(/\/$/, "");
    if (path === itemPath || path === stripped) return true;

    // A PREFIX MATCH YIELDS to an exact one. "My Dashboards" (/admin/report/dashboards) is a prefix of
    // every board under it, so a Rinsed-authored board -- which has its own item in another section --
    // lit both at once. Generic on purpose: any item owning the current path exactly wins over the
    // ancestors that merely contain it.
    if (this.#hasExactMatchElsewhere(itemPath)) return false;

    switch (key) {
      case "support_agent_insights":
        if (path.indexOf("/admin/support_agent/conversations") === 0) return true;
        break;
      case "support_agent_my_agents":
        if (path.indexOf("/admin/support_agent/knowledge_bases") === 0) return true;
        if (path.indexOf("/admin/support_agent/phones") === 0) return true;
        if (path.indexOf("/admin/support_agent/conversation_analysis") === 0) return true;
        break;
      case "ticket_settings":
        if (path.indexOf("/admin/ticket_request_type_reasons") === 0) return true;
        if (path.indexOf("/admin/ticket_resolutions") === 0) return true;
        if (path.indexOf("/admin/zendesk_ticketing_integrations") === 0) return true;
        if (path.indexOf("/admin/email_ticketing_integrations") === 0) return true;
        if (path.indexOf("/admin/routing_rules") === 0) return true;
        break;
      case "loyalty_program_accounts":
        if (/^\/admin\/loyalty_programs\/\d+\/accounts(?:\b|$)/.test(path)) return true;
        break;
      case "loyalty_programs":
        if (/^\/admin\/loyalty_programs\/\d+\/accounts(?:\b|$)/.test(path)) return false;
        break;
      case "campaign_messages":
        if (path.indexOf("/admin/campaign_emails") === 0) return true;
        if (path.indexOf("/admin/campaign_text_messages") === 0) return true;
        break;
      case "membership_search":
        if (path.indexOf("/admin/customers") === 0) return true;
        if (path.indexOf("/admin/memberships") === 0) return true;
        break;
      case "pos_products":
        // A product detail page (/admin/pos_configuration/products/:id) belongs to
        // Product Catalog › Products, so keep that item active and its section open there.
        if (path.indexOf("/admin/pos_configuration/products/") === 0) return true;
        break;
      case "pos_locations":
        // A location detail page (/admin/pos_configuration/locations/:id) belongs to the
        // top-level Locations leaf, so keep that item active there.
        if (path.indexOf("/admin/pos_configuration/locations/") === 0) return true;
        break;
      case "pos_promotions":
        // A promotion detail page (/admin/pos_configuration/promotions/:id) belongs to
        // Product Catalog › Promotions & Discounts, so keep that item active and its section open there.
        if (path.indexOf("/admin/pos_configuration/promotions/") === 0) return true;
        break;
      case "pos_configuration":
        // ...and not to POS Overview, which owns the rest of /admin/pos_configuration.
        if (path.indexOf("/admin/pos_configuration/products/") === 0) return false;
        if (path.indexOf("/admin/pos_configuration/locations/") === 0) return false;
        if (path.indexOf("/admin/pos_configuration/promotions/") === 0) return false;
        break;
    }

    return path.indexOf(stripped) === 0;
  }
}
